import { useState, useCallback, useRef } from 'react';

import type { DocNode as ADF } from '@atlaskit/adf-schema/doc';

import { fg } from '@confluence/feature-gating';

import { detectIntegrationLinks, type DetectedLink, type LinkType } from './link-detector.tsx';

type ADFValue = ADF | null | undefined;

interface UseIntegrationLinkSpotlightReturn {
	detectedLinks: DetectedLink[];
	newLinkTypes: LinkType[];
	newLinks: DetectedLink[];
	shouldShowSpotlight: boolean;
	/** Handle save event - detects new links by traversing entire document and comparing to baseline */
	handleSave: (adfValue: ADFValue) => void;
	/** Handle pasted links - more efficient, only checks pasted links against baseline (used by full-page editor) */
	handlePastedLinks: (links: DetectedLink[]) => void;
	closeSpotlight: () => void;
	setInitialContent: (adfValue: ADFValue) => void;
}

/**
 * Hook to manage the integration link spotlight state for description field
 * Detects NEWLY ADDED integration links by comparing URLs (not just types).
 *
 * We cannot feature-gate the hook invocation (React rules of hooks), but we gate
 * all behaviour and expensive work inside so when the feature is off the hook
 * does minimal work (no link detection, no state updates).
 */
export const useIntegrationLinkSpotlight = (): UseIntegrationLinkSpotlightReturn => {
	const [detectedLinks, setDetectedLinks] = useState<DetectedLink[]>([]);
	const [newLinkTypes, setNewLinkTypes] = useState<LinkType[]>([]);
	const [newLinks, setNewLinks] = useState<DetectedLink[]>([]);
	const [shouldShowSpotlight, setShouldShowSpotlight] = useState(false);

	// Track what URLs existed when editor opened (initial state)
	// This enables URL-level delta detection
	const initialUrlsRef = useRef<Set<string>>(new Set());

	/**
	 * Set the initial content when editor opens
	 * Call with existing ADF for edit mode, or null for new content
	 */
	const setInitialContent = useCallback((adfValue: ADFValue) => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (!fg('link-paste-spotlight-experiment-gate')) {
			return;
		}
		const initialLinks = detectIntegrationLinks(adfValue);
		const initialUrls = new Set(initialLinks.map((link) => link.url));
		initialUrlsRef.current = initialUrls;
	}, []);

	/**
	 * Handle save event - detect NEW links (URL-level) compared to initial content
	 * Key change: If user adds a second Slack link, Slack is still "newly eligible"
	 */
	const handleSave = useCallback((adfValue: ADFValue) => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (!fg('link-paste-spotlight-experiment-gate')) {
			return;
		}

		// Detect all integration links in saved content
		const currentLinks = detectIntegrationLinks(adfValue);

		// Get initial URLs
		const initialUrls = initialUrlsRef.current;

		// Find newly added URLs (not in initial set)
		const newlyAddedLinks = currentLinks.filter((link) => !initialUrls.has(link.url));

		if (newlyAddedLinks.length > 0) {
			// Get unique types from newly added links
			const newTypes = [...new Set(newlyAddedLinks.map((link) => link.type))];

			setDetectedLinks(currentLinks);
			setNewLinks(newlyAddedLinks);
			setNewLinkTypes(newTypes);
			setShouldShowSpotlight(true);
		}
	}, []);

	/**
	 * Handle pasted integration links - detect NEW links (URL-level) compared to initial content
	 * More efficient than handleSave as it only processes pasted links, not entire document
	 */
	const handlePastedLinks = useCallback((links: DetectedLink[]) => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (links.length === 0 || !fg('link-paste-spotlight-experiment-gate')) {
			return;
		}

		// Get initial URLs
		const initialUrls = initialUrlsRef.current;

		// Find newly added URLs (not in initial set) - DELTA LOGIC
		const newlyAddedLinks = links.filter((link) => !initialUrls.has(link.url));

		if (newlyAddedLinks.length > 0) {
			// Get unique types from newly added links
			const newTypes = [...new Set(newlyAddedLinks.map((link) => link.type))];

			setDetectedLinks(newlyAddedLinks);
			setNewLinks(newlyAddedLinks);
			setNewLinkTypes(newTypes);
			setShouldShowSpotlight(true);
		}
	}, []);

	/**
	 * Close the spotlight
	 */
	const closeSpotlight = useCallback(() => {
		setShouldShowSpotlight(false);
		setDetectedLinks([]);
		setNewLinkTypes([]);
		setNewLinks([]);
	}, []);

	return {
		detectedLinks,
		newLinkTypes,
		newLinks,
		shouldShowSpotlight,
		handleSave,
		handlePastedLinks,
		closeSpotlight,
		setInitialContent,
	};
};
